import { ColumnDef } from "@tanstack/react-table";
import { Avatar } from "@mui/material";
import { enumToStringConverter } from "../../../helpers/appHelpers";

// Type definitions
interface Employee {
	firstName: string;
	lastName: string;
	photoUrl?: string;
}

interface PayPeriod {
	code: string;
}

interface Overtime {
	name: string;
}

interface WorkTimeRow {
	id: number;
	employee?: Employee;
	payPeriod?: PayPeriod;
	timeValue: number;
	timeUnit: string;
	createdAt?: string;
	modifiedAt?: string;
}

interface OvertimeManagementRow {
	id: number;
	employee?: Employee;
	numberOfHours: number;
	overtime?: Overtime;
	payPeriod?: PayPeriod;
	createdAt?: string;
	modifiedAt?: string;
}

// ========== WorkTime Columns ==========
export const workTimeColumns: ColumnDef<WorkTimeRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="employee"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{employee.firstName + " " + employee.lastName}
					</p>
				</div>
			);
		},
	},
	{
		id: "payPeriod",
		accessorKey: "payPeriod",
		header: "Pay Period",
		size: 220,
		cell: ({ row }) => row.original.payPeriod?.code || "--",
	},
	{
		id: "timeUnit",
		accessorKey: "timeUnit",
		header: "Time Unit",
		size: 200,
		cell: ({ row }) =>
			`${row.original.timeValue} ${enumToStringConverter(
				row.original.timeUnit
			)}(s)`,
	},
];

export const workTimeColumnVisibility: Record<string, boolean> = {
	employee: true,
	payPeriod: true,
	timeUnit: true,
};

// ========== Overtime Management Columns ==========
export const overtimeManagementColumns: ColumnDef<OvertimeManagementRow>[] = [
	{
		id: "employee",
		accessorKey: "employee",
		header: "Employee",
		size: 250,
		cell: ({ row }) => {
			const employee = row.original.employee;
			if (!employee) return null;

			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={employee.photoUrl || ""}
						alt="employee"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{employee.firstName + " " + employee.lastName}
					</p>
				</div>
			);
		},
	},
	{
		id: "numberOfHours",
		accessorKey: "numberOfHours",
		header: "Number of Hours",
		size: 220,
	},
	{
		id: "overtime",
		accessorKey: "overtime",
		header: "Overtime Type",
		size: 220,
		cell: ({ row }) => row.original.overtime?.name || "--",
	},
	{
		id: "payPeriod",
		accessorKey: "payPeriod",
		header: "Pay Period",
		size: 220,
		cell: ({ row }) => row.original.payPeriod?.code || "--",
	},
];

export const overtimeManagementColumnVisibility: Record<string, boolean> = {
	employee: true,
	numberOfHours: true,
	overtime: true,
	payPeriod: true,
};
